<template>
  <b-navbar class="py-2 justify-content-between" :class="[userLoggedIn ? 'position-sticky' : ['position-fixed', 'w-100', 'shadow-open', brand]]">
    <burger v-if="userLoggedIn" v-model="isNavOpen" :class="{'d-xl-none': hideBurgerMenu}" />
    <b-navbar-brand href="/">
      <img class="ml-md-3 logo" src="/leadsio-logo-white-alt.svg" width="135" alt="Boilerguide Installer Dashboard">
    </b-navbar-brand>

    <b-navbar-nav v-if="userLoggedIn" class="ml-md-auto align-items-center navbar-nav-logged-in font-weight-500">
      <b-nav-text class="d-none d-lg-inline-flex nav__block text-right">
        {{ companyName }}
      </b-nav-text>
      <b-nav-text v-if="!isMobile" class="d-none d-md-inline-flex nav__block">
        <feedback />
      </b-nav-text>
      <b-nav-text class="d-none d-md-inline-flex nav__block">
        <account-balance />
      </b-nav-text>
      <b-nav-text v-if="displayHolidayModeLeadAlertToggle" class="d-none d-md-inline-flex nav__block">
        <lead-alerts variant="light" />
      </b-nav-text>
      <b-nav-item-dropdown
        v-if="displayHolidayModeLeadAlertToggle"
        ref="notificationDropDown"
        :menu-class="['border-0','p-0', 'm-0']"
        class="nav-icon nav__block nav__block--with-icon"
        offset="25"
        no-caret
        right
        @hide="onLeadAlertsClosed"
        @show="$refs.notifications.markRead()"
      >
        <template slot="button-content" class="p-0">
          <font-awesome-icon :icon="['far', 'bell']" size="lg" />
          <span v-if="newNotifications > 0" class="fa-layers-counter">
            {{ newNotifications }}
          </span>
        </template>
        <lead-notifications ref="notifications" @close="$refs.notificationDropDown.hide()">
          <template v-if="isMobile && displayHolidayModeLeadAlertToggle" v-slot:pane-header>
            <lead-alerts variant="dark" @modal-closed="onLeadAlertsConfirmationClosed" @modal-opened="onLeadAlertsConfirmationOpened" />
          </template>
        </lead-notifications>
      </b-nav-item-dropdown>
    </b-navbar-nav>

    <b-navbar-nav v-else class="ml-md-auto align-items-center navbar-nav-logged-out font-weight-500">
      <b-nav-text class="d-md-inline-flex nav__block">
        <a :title="businessUnitContactPhone" :href="`tel: ${businessUnitContactPhone}`" class="nav-link">{{ businessUnitContactPhone }}</a>
      </b-nav-text>
      <b-nav-text class="d-none d-md-inline-flex nav__block">
        <a :title="$t('Contact us')" href="https://www.boilerguide.co.uk/trade/contact/" class="nav-link">{{ $t('Contact us') }}</a>
      </b-nav-text>
    </b-navbar-nav>
  </b-navbar>
</template>

<script>
import Burger from '@/components/menu/Burger.vue';
import AccountBalance from "@/components/header/AccountBalance";
import Feedback from "@/components/header/Feedback";
import LeadAlerts from "@/components/header/LeadAlerts";
import LeadNotifications from "@/components/pane/LeadNotifications";
import userDevice from "@/imports/mixins/userDevice";
import companyAccount from "@/imports/mixins/companyAccount";

export default {
  name: "HeaderBar",
  components: {LeadNotifications, Burger, LeadAlerts, AccountBalance, Feedback},
  mixins: [userDevice, companyAccount],
  props: ['value', 'hideBurgerMenu'],
  data() {
    return {
      isOpen: this.value ?? false,
      ignoreLeadNotificationsClosed: false,
      newNotifications: 0,
    }
  },
  computed: {
    userLoggedIn: function() {
      return this.$store.state.USER_STATE;
    },
    notifications: function () {
      return this.$store.state.leads.LEAD_NOTIFICATIONS;
    },
    companyName: function() {
      return (this.$store.state.company.COMPANY) ? this.$store.state.company.COMPANY.internal_name : '';
    },
    brand: function() {
      return this.$route.meta.brand ?? '';
    },
    businessUnitContactPhone: function() {
      return this.$store.state.businessUnitContactDetails.BUSINESS_UNIT_CONTACT_DETAILS?.phone;
    },
    isNavOpen: {
      get() {
        return this.isOpen;
      },
      set(value) {
        this.isOpen = value;
      }
    }
  },
  methods: {
    onLeadAlertsConfirmationOpened: function() {
      this.ignoreLeadNotificationsClosed = true;
    },
    onLeadAlertsConfirmationClosed: function() {
      this.$nextTick(() => this.ignoreLeadNotificationsClosed = false);
    },
    onLeadAlertsClosed: function(e) {
      if (this.ignoreLeadNotificationsClosed) {
        e.preventDefault();
      }
    },
    countNotification: function () {
      this.newNotifications = 0;
      Object.values(this.notifications).forEach((group) => {
        Object.values(group).forEach(leadSet => {
          leadSet.forEach(lead => {
            if (lead.dashboard_lead === null || !lead.dashboard_lead.notification_viewed) {
              this.newNotifications ++;
            }
          })
        })
      });
    }
  },
  watch: {
    value: function(value) {
      this.isNavOpen = value;
    },
    isNavOpen: function(value) {
      this.$emit("input", value)
    },
    notifications: {
      handler: function() {
        this.countNotification();
      },
      deep: true,
      immediate: true,
    }
  },
  created: function () {
    this.$eb.$on('leadNotification-closeDropdown', () => {
      this.$refs.notificationDropDown.hide()
    });
    this.countNotification();
  },
  beforeDestroy: function() {
    this.$eb.$off('leadNotification-closeDropdown')
  }
}
</script>

<style scoped lang="scss">
  .nav-icon {
    &::v-deep {
      .svg-inline--fa {
        @include media-breakpoint-down(sm) {
          font-size: 1.2rem;
        }
        @include media-breakpoint-up(md) {
          font-size: 1.399rem;
        }
      }

      .fa-layers-counter {
        top: -3px;
        background: $bell-notification-count-bg;
        padding: .2rem;

        @include media-breakpoint-down(sm) {
          font-size: 1.1rem;
          right: 3px;
          border: 2px solid $white;
          transform: scale(0.45);
        }
        @include media-breakpoint-up(md) {
          border: 6px solid $white;
          @include font-size(40px);
          right: 18px;
        }
      }
    }
  }

  ::v-deep .dropdown {
    .dropdown-menu {
      display: block;
      visibility: hidden;
      opacity: 0;
      transition: opacity .33s linear;
    }

    &.show {
      .dropdown-menu {
        height: auto;
        visibility: visible;
        opacity: 1;
        transition-delay: 0s;

        .notification-container {
          padding-top: .5rem;
          padding-bottom: .5rem;
        }
      }
    }
  }

  .navbar-expand {
    &::v-deep {
      .navbar-nav .dropdown-toggle-no-caret {
        margin: 0 auto;
        padding: 0;
      }
      .navbar-nav-logged-in {
        .show>.nav-link {
          color: $notification-icon-color;
        }
        .nav-link {
          &:focus,
          &:active,
          &:hover {
            color: $notification-icon-color;
          }
        }
      }
      .navbar-nav-logged-out {
        .nav-link {
          &:focus,
          &:active,
          &:hover {
            color: $white;
          }
        }
      }
      .navbar-nav {
        .dropdown-menu {
          background: $theme-separation-lines-1;

          @include media-breakpoint-up(md) {
            top: 50px;
          }
          @include media-breakpoint-down(sm) {
            position: fixed;
            top: 61px;
            width: 100%;
            height: 100%;
            left: 0;
            right: 0;
            border-radius: 0;
          }
        }
      }
    }
  }

  ::v-deep .nav__block--with-icon.show {
    position: relative;
    &:before {
      content: '\200C';
      position: absolute;
      height: 5px;
      background: $theme-primary-accents-900;
      width: 100%;
      @include media-breakpoint-up(sm) {
        bottom: -21px;
      }
      @include media-breakpoint-down(sm) {
        bottom: -16px;
      }

      left: 0;
    }
  }

  .navbar {
    z-index: 12;
    top: 0;
  }

  .shadow-open {
    box-shadow: 0 0.1875rem 0.375rem rgba(0,0,0,.1);
  }
</style>
